import { useMemo } from 'react';
import { useGoogleMaps } from '@shared/core/google-maps/useGoogleMaps';
import type { LatLng, LatLngBounds, LatLngLiteral, Map } from '@shared/core/google-maps/types';
import { milesToMeters } from '@shared/utils/milesToMeters';
import { ACCOMMODATIONS_DEFAULT_LOCATION } from '@shared/constants/accommodations';

const centerAndZoom = (googleMaps: typeof google.maps) => (center: LatLng | LatLngLiteral, miles: number, map: Map) => {
  // Fit map to a specific mile radius around a center point
  const circle = new googleMaps.Circle({
    center,
    radius: milesToMeters(miles),
    fillOpacity: 0,
    fillColor: 'white',
    strokeWeight: 0,
    map,
  });
  if (circle.getBounds()) {
    map.fitBounds(circle.getBounds()!);
    // Remove circle after map is set to proper bounds
    circle.setMap(null);
  }
};

// Minimum padding in degrees to prevent excessive zoom when all markers are at the same location.
// 0.005° ≈ 500m at the equator - provides enough surrounding context (nearby streets, landmarks)
// without zooming out so far that the marker becomes insignificant.
const MIN_BOUNDS_PADDING = 0.005;

export type GetCalculatedCenterAndZoomOptions = Readonly<{
  /** Extra padding in degrees on all sides so pin graphics (drawn above anchor) are not cropped. */
  boundsPaddingDegrees?: number;
  /** Minimum bounds padding in degrees. Defaults to 0.005 (~500m). Set to 0 for tightest possible fit. */
  minBoundsPadding?: number;
}>;

const getCalculatedCenterAndZoom =
  (googleMaps: typeof google.maps) =>
  (markers: ReadonlyArray<LatLngLiteral>, options?: GetCalculatedCenterAndZoomOptions): Readonly<{ center: LatLngLiteral; bounds: LatLngBounds }> => {
    // Initialize with first marker or default location if empty
    const first = markers?.[0] || { lat: ACCOMMODATIONS_DEFAULT_LOCATION.lat, lng: ACCOMMODATIONS_DEFAULT_LOCATION.long };
    let minLat = first.lat;
    let maxLat = first.lat;
    let minLng = first.lng;
    let maxLng = first.lng;

    // Update bounds for all remaining markers
    for (let i = 1; i < markers.length; i++) {
      const { lat, lng } = markers[i];

      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;

      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    const minPadding = options?.minBoundsPadding ?? MIN_BOUNDS_PADDING;
    const latSpan = Math.max(maxLat - minLat, minPadding);
    const lngSpan = Math.max(maxLng - minLng, minPadding);

    let swLat = centerLat - latSpan / 2;
    let neLat = centerLat + latSpan / 2;
    let swLng = centerLng - lngSpan / 2;
    let neLng = centerLng + lngSpan / 2;

    const padding = options?.boundsPaddingDegrees ?? 0;
    if (padding > 0) {
      swLat -= padding;
      neLat += padding;
      swLng -= padding;
      neLng += padding;
    }

    return {
      center: { lat: centerLat, lng: centerLng },
      bounds: new googleMaps.LatLngBounds(new googleMaps.LatLng(swLat, swLng), new googleMaps.LatLng(neLat, neLng)),
    };
  };

const reduceBounds = (googleMaps: typeof google.maps) => (bounds: LatLngBounds, reductionFactor: number) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  const latReduction = (northEast.lat() - southWest.lat()) * reductionFactor;
  const lngReduction = (northEast.lng() - southWest.lng()) * reductionFactor;

  const reducedNorthEast = new googleMaps.LatLng(northEast.lat() - latReduction, northEast.lng() - lngReduction);
  const reducedSouthWest = new googleMaps.LatLng(southWest.lat() + latReduction, southWest.lng() + lngReduction);

  const reducedBounds = new googleMaps.LatLngBounds(reducedSouthWest, reducedNorthEast);
  return reducedBounds;
};

const getDistanceInMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const getRadiusFromBounds = (googleMaps: typeof google.maps) => (bounds: LatLngBounds) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  const centerLat = (northEast.lat() + southWest.lat()) / 2;
  const centerLng = (northEast.lng() + southWest.lng()) / 2;

  const radius = getDistanceInMeters(centerLat, centerLng, northEast.lat(), northEast.lng());

  const radiusInKm = radius / 1000;

  return { center: new googleMaps.LatLng(centerLat, centerLng), radius: radiusInKm.toFixed(0) };
};

// Helps typescript understand when the functions are available
type GoogleMapsUtils =
  | {
      centerAndZoom: null;
      getCalculatedCenterAndZoom: null;
      reduceBounds: null;
      getDistanceInMeters: null;
      getRadiusFromBounds: null;
    }
  | {
      centerAndZoom: ReturnType<typeof centerAndZoom>;
      getCalculatedCenterAndZoom: ReturnType<typeof getCalculatedCenterAndZoom>;
      reduceBounds: ReturnType<typeof reduceBounds>;
      getDistanceInMeters: typeof getDistanceInMeters;
      getRadiusFromBounds: ReturnType<typeof getRadiusFromBounds>;
    };

export const useGoogleInteractableMap = (): GoogleMapsUtils => {
  const [googleMaps] = useGoogleMaps({ loadOnMount: true });

  return useMemo(() => {
    if (!googleMaps) {
      return {
        centerAndZoom: null,
        getCalculatedCenterAndZoom: null,
        reduceBounds: null,
        getDistanceInMeters: null,
        getRadiusFromBounds: null,
      };
    }
    return {
      centerAndZoom: centerAndZoom(googleMaps),
      getCalculatedCenterAndZoom: getCalculatedCenterAndZoom(googleMaps),
      reduceBounds: reduceBounds(googleMaps),
      getDistanceInMeters,
      getRadiusFromBounds: getRadiusFromBounds(googleMaps),
    };
  }, [googleMaps]);
};
